import classNames from 'classnames';
import React, { ChangeEvent, FocusEvent, memo, useMemo } from 'react';
import Div from './Div';
import Label from './Label';
import { FieldError, FieldErrors } from 'react-hook-form';
import VALIDATION_ERRORS from '@/constants/validationErrors';
import { inputOnlyNumbers } from '@/utils/formValidation';

const TEXTAREA_SIZES = {
  xs: '',
  sm: '',
  base: 'text-body-lg py-3 px-4',
  lg: '',
  xl: '',
};

type TextareaProps = {
  className?: string;
  id?: string | number;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
  label?: string;
  placeholder?: string;
  name: string;
  maxLength?: number;
  errors?: FieldErrors;
  value?: string | number;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  dataTestId?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  wrapperClassName?: string;
  autoFocus?: boolean;
  autoComplete?: string;
};

// eslint-disable-next-line react/display-name
const Textarea = React.forwardRef(
  (
    {
      name,
      id,
      className,
      label,
      errors,
      disabled,
      placeholder,
      size = 'base',

      dataTestId,

      style,
      autoFocus,
      autoComplete,
      onKeyDown,
      // wrapperClassName,            //Ravi to look into it
      ...restProps
    }: TextareaProps,
    ref: React.Ref<HTMLTextAreaElement>,
  ) => {
    const fieldError: FieldError | undefined = (errors?.[name] as FieldError) ?? undefined;
    const errorMessage = fieldError?.message || VALIDATION_ERRORS[fieldError?.type];

    const inputClasses = useMemo(
      () =>
        classNames(
          'border border-neutral-100 text-black/70 placeholder:text-black/30 foucs:outline focus:outline-blue-300 bg-white transition-all rounded-cxs',
          {
            [TEXTAREA_SIZES[size]]: true,
            ['border-red-500']: errorMessage,
          },
          className,
          {
            '!text-black/30': disabled,
          },
        ),
      [size, className, errorMessage, disabled],
    );

    return (
      <Div
        className={classNames('grid w-full', {
          'gap-s3': label || errorMessage,
        })}
      >
        <Label htmlFor={name}>{label}</Label>
        <textarea
          data-testid={dataTestId}
          ref={ref}
          name={name}
          id={name}
          autoComplete={autoComplete ?? 'off'}
          disabled={disabled}
          placeholder={placeholder}
          {...restProps}
          className={inputClasses}
          style={style}
          autoFocus={autoFocus}
        />
        <Div
          forceRender
          className={classNames('text-body-base text-neutral-700', {
            'text-red-500': errorMessage,
          })}
        >
          {errorMessage}
        </Div>
      </Div>
    );
  },
);

export default memo(Textarea);
